// DoorType is defined in door-colour-select.ts (the module that renders the
// tiers) and imported here rather than re-spelled. It was duplicated as an
// inline union in both places below, and adding the `paintplus` tier on
// 2026-09-09 broke on exactly that drift.
import type { DoorType } from "./door-colour-select";

// Quote Builder — domain types.
//
// Mirrors the v3 quote shape used by the legacy kos-qb-v3.js
// (see _PLAN.md for the legacy line refs). Kept compatible with the
// existing localStorage payload (`kos_quote_builder_v3`) so retailers
// who minimised a quote in the old portal can resume in the new one.

export type QbStatus = "draft" | "sent" | "approved" | "ordered";
export type GroupBy = "zone" | "supplier";
export type MarkupMode = "pct" | "gbp";
export type Hinging = "L" | "R" | "B" | "N" | "";

/** Door drilling defaults — verbatim kos-qb-v3.js:182–183. */
export const DEFAULT_TOP_MM = 100;
export const DEFAULT_BOTTOM_MM = 100;

/** TKC tab display vocabulary — verbatim kos-qb-v3.js:1707–1711. The TKC
 *  catalogue API returns flat product names (no category field), so the
 *  Products panel chips and the result grouping both come from
 *  `tkcInferCategory(name)` regex-classifying each product. */
export const TKC_CATEGORY_ORDER = [
  "Doors",
  "Cabinets",
  "Boxed Drawers",
  "Drawer Components",
  "Panels",
  "Plinth & Cornices",
  "Handles",
  "Quick Cabs",
  "Vero Accessories",
  "Worktops",
  "Storage Solutions",
  "Sinks & Taps",
  "Appliances",
  "Lighting",
  "Accessories",
  "Accessories & Sundries",
] as const;

export type TkcDisplayCategory = (typeof TKC_CATEGORY_ORDER)[number];

/** Per-TKC-category seed search term — used when the user picks a chip
 *  without typing a query. Verbatim kos-qb-v3.js:1886–1897 catSearchTerms. */
export const TKC_CATEGORY_SEARCH_TERMS: Record<TkcDisplayCategory, string> = {
  "Doors":                 "door",
  "Cabinets":              "base unit",
  "Boxed Drawers":         "boxed drawer",
  "Drawer Components":     "drawer",
  "Panels":                "end panel",
  "Plinth & Cornices":     "plinth",
  "Handles":               "handle",
  "Quick Cabs":            "carcase",
  "Vero Accessories":      "vero",
  "Worktops":              "worktop",
  "Storage Solutions":     "pull out",
  "Sinks & Taps":          "sink",
  "Appliances":            "oven",
  "Lighting":              "lighting",
  "Accessories":           "accessory",
  "Accessories & Sundries":"accessory",
};

/** Map TKC display categories to the canonical ItemCategory written on
 *  quote items. Source kos-qb-v3.js stores items with `category: ''`
 *  (the TKC API doesn't return a category field), but modular's
 *  ItemCategory is required + typed — so each TKC chip maps to its
 *  nearest canonical bucket. 'Sinks & Taps' has no canonical equivalent,
 *  so it falls through to 'sundries' (matching source's catch-all). */
export const TKC_CATEGORY_TO_ITEM: Record<TkcDisplayCategory, ItemCategory> = {
  "Doors":                  "doors",
  "Cabinets":               "cabinets",
  "Boxed Drawers":          "storage",
  "Drawer Components":      "storage",
  "Panels":                 "panels",
  "Plinth & Cornices":      "plinth",
  "Handles":                "handles",
  "Quick Cabs":             "cabinets",
  "Vero Accessories":       "sundries",
  "Worktops":               "worktops",
  "Storage Solutions":      "storage",
  "Sinks & Taps":           "sundries",
  "Appliances":             "appliances",
  "Lighting":               "lighting",
  "Accessories":            "sundries",
  "Accessories & Sundries": "sundries",
};

/** Canonical item categories used by the picker tabs and the AI tool. */
export type ItemCategory =
  | "cabinets"
  | "doors"
  | "panels"
  | "plinth"
  | "storage"
  | "handles"
  | "worktops"
  | "lighting"
  | "appliances"
  | "sundries"
  | "labour";

export const ITEM_CATEGORIES: readonly ItemCategory[] = [
  "cabinets",
  "doors",
  "panels",
  "plinth",
  "storage",
  "handles",
  "worktops",
  "lighting",
  "appliances",
  "sundries",
  "labour",
];

/** Supplier registry — mirror of `SUPPLIER_MODELS` in kos-qb-v3.js:58. */
export interface SupplierDef {
  /** Tab id used in the UI (matches source `PROD_TABS[i].id`). */
  key: string;
  /** Display label on the pill (defaults to `key` when omitted). */
  label?: string;
  /** Supplier string written to QuoteItem.supplier when this tab adds a line.
   *  Distinct from `key` for DTN ("Trade Kitchens by DTN") and CUSTOM ("Custom"). */
  supplierKey: string;
  /** "completes" = supplier ships full units; "mixmatch" = a-la-carte. */
  model: "completes" | "mixmatch";
  /** Brand colour. */
  colour: string;
  /** Default global discount % applied to fresh items from this supplier. */
  defaultDiscPct: number;
  /** Tab mode — "door-ranges" pre-loads the supplier's ranges before search;
   *  "live" runs a server search per keystroke. Mirrors source PROD_TABS.mode. */
  mode: "door-ranges" | "live";
  /** Live-search endpoint path under /api. Empty for door-ranges-only tabs. */
  api?: string;
  /** When set, this is a catalogue_products-backed supplier and the value is the
   *  `suppliers.slug` used as `/catalogue/search?type=supplier&id=<slug>`. These
   *  tabs are DYNAMIC + per-tenant access-gated (see /api/catalogue/suppliers);
   *  any imported catalogue appears automatically once an admin grants it. */
  catalogueSlug?: string;
  /** Help text from source `SUPPLIER_MODELS.note`. */
  note?: string;
}

/** Quote Builder Products-panel tab bar. The catalogue_products-backed tabs
 *  (TKC + any imported supplier such as PWS) are now DYNAMIC + access-gated —
 *  see loadCatalogueTabs() in products-panel.ts. DTN/LDL (external live APIs)
 *  and CUSTOM (the retailer's own catalogue) are always-on. TKC keeps its rich
 *  door-ranges UI; other catalogue suppliers render as flat search tabs. */
export const SUPPLIERS: readonly SupplierDef[] = [
  { key: "TKC",    supplierKey: "TKC",                     model: "completes", colour: "#e58200", defaultDiscPct: 0,  mode: "door-ranges", api: "/catalogue/search", catalogueSlug: "tkc" },
  { key: "DTN",    supplierKey: "Trade Kitchens by DTN",   model: "completes", colour: "#16a34a", defaultDiscPct: 0,  mode: "live",        api: "/dtn/search",
    note: "+15% retailer markup pre-applied; default discount 0%" },
  { key: "LDL",    supplierKey: "LDL",                     model: "mixmatch",  colour: "#0ea5e9", defaultDiscPct: 0,  mode: "live",        api: "/ldl/search",
    note: "Customer List = retailer trade price; no separate list/RRP" },
  // SJB's own 2026 cabinet catalogue. `mode: "live"` but the endpoint is not a
  // plain price list: an SJB cabinet has no single price, because the carcass
  // moves with the quote's board colour and hinges/drawer boxes ride on top at
  // full list. products-panel routes this tab through sjb-catalogue.ts rather
  // than the shared normalise/addItem path for that reason.
  { key: "SJB",    supplierKey: "SJB Cabinets",
                                                            model: "completes", colour: "#a16207", defaultDiscPct: 0,  mode: "live",        api: "/sjb-catalogue/cabinets",
    note: "Board colour set per quote; carcass discount does not apply to hinges or drawer boxes" },
  { key: "CUSTOM", supplierKey: "Custom", label: "My Catalogue",
                                                            model: "mixmatch",  colour: "#e58200", defaultDiscPct: 0,  mode: "live",        api: "/catalogue/search" },
];

/** Per-category defaults persisted on the quote — verbatim source
 *  defaultDefaults() at kos-qb-v3.js:201–216. The picker UIs (Items 4–5)
 *  seed each fresh picker session from these, and write back when their
 *  matching "Remember" toggle is on. */
export interface DoorDefaults {
  supplier: string;
  range: string;
  finish: string;
  drilling: { hingeCount: number; topMm: number; bottomMm: number; side: "L" | "R" | "T" };
  integratedHandle: "none" | "base" | "wall";
  // ── Additive (correct picker — renderDoorConfigurator port) ────────────
  /** Remembered tier from last add. */
  doorType?: DoorType;
  /** Remembered unit type. */
  doorUnitType?: "base" | "wall";
  /** Remembered drilling mode toggle (None | 35mm Hinge). */
  drillMode?: "none" | "hinge";
}

export interface CabinetDefaults {
  supplier: string;
  /** Source verbatim: 'standard' or 'true-handleless'. Fixes Item 2's
   *  incorrect 'rigid' guess (source line 4240 uses 'true-handleless'). */
  construction: "standard" | "true-handleless";
  carcassFinish: string;
  carcassFinishCode: string;
  carcassPriceGroup: number;
  edging: string;
  edgingCode: string;
  hinging: "L" | "R" | "B" | "N";
}

/** Quote-level defaults for the SJB cabinet catalogue (2026 DTN price file).
 *
 *  Board colour, hinge finish and drawer system are all "set once, inherited
 *  by each new line, editable per line". They live here — on the quote's
 *  `defaults` JSONB — rather than in localStorage like the older DTN session
 *  strip (`kos_session_finish_DTN`), for two reasons:
 *
 *    1. The LS key is browser-global, so it is shared by every quote open on
 *       that machine. Changing the colour while pricing quote B would move
 *       quote A's default too.
 *    2. It determines line pricing, which makes it business data, not a UI
 *       preference.
 *
 *  Follows the `markupPct` precedent directly above: seeded into new lines
 *  only, never applied retroactively — see the note there. */
export interface SjbDefaults {
  /** BRAND|COLOUR|STRUCTURE, matching /api/sjb-catalogue/colours `key`.
   *  Structure is part of the identity because brand+colour alone collide
   *  (Kronospan Black is 30% off in PE and PLUS 20 in MG). */
  boardKey: string;
  /** Display label cached alongside the key so a line renders without a
   *  round-trip to the colours endpoint. */
  boardLabel: string;
  hingeFinish: "standard" | "onyx";
  drawerSystem: "antaro" | "legrabox";
  /** Front edging colour, stored as the decor's LABEL (display text), or
   *  "Matching carcass" — the default.
   *
   *  SUPERSEDED 2026-09-05: was EDGING_OPTIONS, the platform's 11-entry
   *  ABS/veneer list shared with DTN. Now the same 614 board decors as the
   *  carcass, keyed brand|colour|structure — Steve confirmed every decor SJB
   *  stock has matching tape. See SJB_EDGING_MATCHING in sjb-catalogue.ts for
   *  why it carries no discount/uplift. */
  edgingColour: string;
  /** Whether the SJB picker offers frontals at all.
   *
   *  Cabinets-only is a property of the JOB, not of each cabinet: a retailer
   *  who sources doors elsewhere sources them for the whole kitchen, and
   *  re-answering per cabinet is 30 identical clicks and one forgotten
   *  toggle away from a kitchen with no fronts. So it rides on the quote's
   *  defaults like colour/hinge/drawer system — NOT in localStorage, which is
   *  browser-global and would leak a cabinets-only job into the next quote.
   *
   *  Defaults to true: doors-on matches every quote built before this field
   *  existed, and a missing frontal is a far louder failure than an unwanted
   *  one. */
  frontalOn: boolean;
  /** Which door catalogue the frontals come from — the slug, e.g. "tkc" or
   *  "pws", as listed by GET /sjb-catalogue/frontal-suppliers.
   *
   *  ALWAYS REMEMBERED, and deliberately NOT gated by `rememberFrontal`.
   *  That flag exists because a range or a colour can legitimately differ
   *  between cabinets — a different door on an island, say. The door
   *  CATALOGUE effectively never does: a retailer who buys fronts from PWS
   *  buys the whole kitchen's fronts from PWS. Same argument as `frontalOn`
   *  above, and the cost of getting it wrong is the same shape — Steve had to
   *  reselect the supplier on every single unit of his first real quote
   *  (2026-09-10) before this existed.
   *
   *  Stored as the SLUG ONLY, never the display label. Labels come from
   *  server config (SJB_FRONTAL_SUPPLIERS) and have changed before — TKC
   *  displays as "On Trend" for some tenants — so a cached label would go
   *  stale against a quote that outlives the config. The picker resolves the
   *  label from the live supplier list. */
  frontalSlug: string;
  /** Remembered frontal Range and Colour, and the toggle that governs them.
   *
   *  Same mechanism as the TKC Door Configurator's "Remember" (door-picker.ts
   *  405/568 — flag on the quote, value written at pick time when the flag is
   *  on), but stored on `defaults.sjb` rather than `defaults.door`.
   *
   *  Kept separate deliberately. An SJB frontal IS a TKC door, so sharing
   *  `defaults.door` was tempting — but it would mean configuring a door in
   *  the TKC picker silently re-pointed the SJB picker and vice versa, which
   *  is a surprise in a place where a wrong range is a whole kitchen of wrong
   *  fronts. `frontalOn` already lives here for the same reason.
   *
   *  Range and Colour ONLY. Size and drilling stay per-cabinet — a 1000HL and
   *  a 300CBB do not take the same door, and remembering a size would be
   *  actively wrong (Steve, 2026-09-05). */
  frontalRange: string;
  frontalColour: string;
  /** The colour's pricing tier, carried so the remembered colour re-selects
   *  in the tier-grouped control rather than looking absent. */
  frontalTier: string;
  rememberFrontal: boolean;
}

export interface HandleDefaults   { supplier: string; }
export interface StorageDefaults  { supplier: string; subType: string; }
export interface WorktopDefaults  { supplier: string; material: string; }
export interface PanelDefaults    { supplier: string; type: string; }
export interface PlinthDefaults   { supplier: string; type: string; }
export interface LightingDefaults { supplier: string; type: string; }
export interface ApplianceDefaults{ supplier: string; type: string; }
export interface SundriesDefaults { supplier: string; type: string; }
export interface LabourDefaults   { rate: number; }

/** "Remember me" toggles — when on, the matching picker writes its last
 *  used value back to the matching field in `QuoteDefaults`. Verbatim
 *  shape from source kos-qb-v3.js:214. */
export interface RememberToggles {
  edging: boolean;
  drilling: boolean;
  carcassFinish: boolean;
  range: boolean;
  finish: boolean;
  construction: boolean;
}

export interface QuoteDefaults {
  door: DoorDefaults;
  cabinet: CabinetDefaults;
  handle: HandleDefaults;
  storage: StorageDefaults;
  worktop: WorktopDefaults;
  panel: PanelDefaults;
  plinth: PlinthDefaults;
  lighting: LightingDefaults;
  appliance: ApplianceDefaults;
  sundries: SundriesDefaults;
  labour: LabourDefaults;
  remember: RememberToggles;
  /** Quote-level default markup % seeded into every new line item.
   *  Changing this only affects FUTURE adds — existing lines keep their
   *  stored markupPct (verified: wireMeta does not iterate items).
   *  Stashed in this JSONB-backed defaults object rather than as a
   *  top-level quote column so the server round-trips it for free via the
   *  existing `defaults` JSONB plumbing (kitchenos-api/modules/portal_quotes.js:144).
   *  Approved by Steve 2026-05-15. */
  markupPct: number;
  /** SJB cabinet catalogue defaults. Optional so quotes saved before this
   *  shipped round-trip unchanged. */
  sjb?: SjbDefaults;
}

export interface QuoteItem {
  id: string;
  category: ItemCategory;
  supplier: string;
  code: string;
  desc: string;
  qty: number;
  /** All dimensions in millimetres. 0 = unspecified. */
  width: number;
  height: number;
  depth: number;
  notes: string;
  // ── Pricing (display GBP; aggregation done in pence — see pricing.ts) ──
  listPrice: number;
  discPct: number;
  tradePrice: number;
  markupMode: MarkupMode;
  markupPct: number;
  markupGbp: number;
  retailPrice: number;
  // Snapshot of the markup BEFORE a "Set Total Profit" target was applied, so
  // the Reset button can restore each line's original margin. Captured on the
  // first apply, cleared on reset.
  preTargetMarkupMode?: MarkupMode;
  preTargetMarkupPct?: number;
  preTargetMarkupGbp?: number;
  // ── Category-specific (optional) ──
  doorRange?: string;
  doorColour?: string;
  /** Jigsaw cabinet carcase colour (Oak / White / Light Grey / Dust Grey).
   *  Distinct from `carcassFinish` (DTN board finish) — set by the Jigsaw
   *  cabinet picker, which sources it from catalogue_carcase_colours. */
  carcaseColour?: string;
  carcassFinish?: string;
  edging?: string;
  hinging?: Hinging;
  material?: string;
  length?: number;
  thickness?: number;
  unitTypeId?: string;
  unitTypeLabel?: string;
  // ── Cabinet picker output (kos-qb-v3.js:4433–4452) ─────────────────────
  construction?: "standard" | "true-handleless";
  variant?: string;
  blank?: number;
  carcassFinishCode?: string;
  carcassPriceGroup?: 1 | 2 | 3 | 4 | 5;
  edgingCode?: string;
  // ── Door picker output (kos-qb-v3.js:4140–4163) ────────────────────────
  doorColourHex?: string;
  mtm?: boolean;
  hingeCount?: number;
  topMm?: number;
  bottomMm?: number;
  leftMm?: number;
  rightMm?: number;
  drillingPositions?: number[];
  drillingFrom?: "" | "top" | "edges";
  liftUp?: boolean;
  handing?: "left" | "right" | "top" | "lift-up" | "";
  integratedHandle?: "none" | "base" | "wall";
  // ── Correct picker output (kos-qb-v3.js:9753 _qbv3w6AddDoor) ───────────
  doorType?: DoorType;
  doorBespokeText?: string;
  doorUnitType?: "base" | "wall";
  doorHoleQty?: number;
  doorHoleMidMm?: number[];
  // ── W8 cabinet picker output (kos-qb-v3.js:11551 _qbv3w8AddCabinet) ────
  /** Additive only — Phase 1 of the W8 picker swap (2026-05-15). Existing
   *  consumers (non-DTN paths, PDF, place-order) ignore these fields. */
  drawerConfig?: string;
  drawerBox?: string;
  drawersBelow?: string;
  gableReduction?: string;
  isDrawerline?: boolean;
  topDoor?: string;
  bottomDoor?: string;
  // ── SJB cabinet catalogue (2026 DTN price file) ────────────────────────
  /** Present only on lines added from the SJB catalogue. When set,
   *  `listPrice`/`discPct` describe the CARCASS ONLY and `sjbHardwareTotal`
   *  carries the hinges and drawer boxes at full list — the board adjustment
   *  does not apply to them. pricing.ts:recalcItem adds the two together, so
   *  nothing downstream needs to know about the split. */
  sjbCode?: string;
  /** Snapshot of the colour on THIS line. Seeded from QuoteDefaults.sjb at
   *  add time and then owned by the line: changing the quote default later
   *  must not touch it. */
  sjbBoardKey?: string;
  sjbBoardLabel?: string;
  sjbHingeFinish?: "standard" | "onyx";
  sjbDrawerSystem?: "antaro" | "legrabox";
  sjbEdgingColour?: string;
  /** Reduced gable side (column 7) and blanking panel side (column 9).
   *  NOT PRICED — SJB's files carry no charge for either, confirmed by Steve
   *  2026-09-07, so both are set after the line is priced and neither can
   *  move a number. They also ride on `notes`, which is what the quote row
   *  and the internal PDF render; the PO export keeps only
   *  {code, description, qty, tradePence, zone}, so like hinge side and
   *  edging they do NOT currently reach the supplier order. */
  sjbReducedGable?: string;
  sjbBlankingPanel?: string;
  /** Hinges + drawer boxes for this line, at full list. */
  sjbHardwareTotal?: number;
  /** Itemised hardware, for the line detail panel and the internal PDF. */
  sjbHardware?: SjbHardwareComponent[];
  /** e.g. "45% board discount" / "+100% board uplift". Named this way
   *  deliberately: a blanket "45% off list" badge would imply the saving
   *  applies to the hardware too. */
  sjbDiscountLabel?: string;
  /** Links the lines committed together by one pass through the SJB cabinet
   *  picker — the carcass, its frontal, and any drilling charge. They stay
   *  SEPARATE lines so the frontal keeps the ordinary door pricing path (its
   *  own supplier discount and drilling charge); this only groups them for
   *  display. Set on every line in the group, including the frontal, which is
   *  otherwise an ordinary `doors` line. */
  sjbGroupId?: string;
  /** True on the cabinet line that heads a group — lets the quote list render
   *  the frontal indented beneath it without inferring order. */
  sjbGroupHead?: boolean;
}

/** One priced hardware component on an SJB line. */
export interface SjbHardwareComponent {
  kind: "hinge" | "drawer";
  code: string;
  desc: string;
  qty: number;
  unitPrice: number;
  price: number;
}

/** Cabinet type definition — verbatim CABINET_TYPES at kos-qb-v3.js:102–146.
 *  `cat` is the top-level Base/Wall/Tall/Dresser bucket; `widths` are the
 *  standard widths (mm), custom values always allowed; `tallHeights` only
 *  applies to Tall units; `heights` only applies to certain Wall units;
 *  `variants` is the drawer-pack variant list; `blanks` is the corner
 *  filler size list. */
export interface CabinetType {
  id: string;
  label: string;
  cat: "Base" | "Wall" | "Tall" | "Dresser";
  widths: number[];
  h: number;
  d: number;
  heights?: number[];
  tallHeights?: number[];
  variants?: string[];
  blanks?: number[];
}

export const CABINET_TYPES: readonly CabinetType[] = [
  /* ─── BASE (standard depth 570) ─── */
  { id: "base-highline",     label: "Highline Base",           cat: "Base", widths: [150,260,300,350,400,450,500,550,600,700,800,900,1000,1200], h: 720, d: 570 },
  { id: "base-drawerline",   label: "Drawerline Base",         cat: "Base", widths: [400,450,500,600,800,900,1000,1200], h: 720, d: 570 },
  { id: "base-drawerpack",   label: "Drawerpack",              cat: "Base", widths: [400,450,500,600,800,900,1000], h: 720, d: 570,
    variants: ["2 Pan Drawers", "2 + Internal Pan Drawers", "1 + 2 Pan Drawers", "2 + 1 Pan Drawer", "3 + 1 Pan Drawer", "4 Drawers", "5 Drawers"] },
  { id: "base-corner-hl",    label: "Corner Base · Highline",  cat: "Base", widths: [800,900,1000,1100,1200], h: 720, d: 900,
    blanks: [300,350,400,450,500,550,600] },
  { id: "base-corner-dl",    label: "Corner Base · Drawerline",cat: "Base", widths: [800,900,1000,1100,1200], h: 720, d: 900,
    blanks: [300,350,400,450,500,550,600] },
  { id: "base-corner-l",     label: "Corner Base · L-Shaped",  cat: "Base", widths: [900], h: 720, d: 900 },
  { id: "base-sink",         label: "Base Sink Unit",          cat: "Base", widths: [600,800,900,1000,1200], h: 720, d: 570 },
  { id: "base-oven-housing", label: "Oven Housing (Base)",     cat: "Base", widths: [600], h: 720, d: 570 },
  { id: "base-end-curved",   label: "End Unit · Curved",       cat: "Base", widths: [300,350,400], h: 720, d: 570 },
  { id: "base-end-angled",   label: "End Unit · Angled",       cat: "Base", widths: [300,350,400], h: 720, d: 570 },
  { id: "base-open",         label: "Open / Pull Out Base",    cat: "Base", widths: [150,260,300,350,400,450,500,550,600,700,800,900,1000,1200], h: 720, d: 570 },
  { id: "base-wine",         label: "Wine Rack (Base)",        cat: "Base", widths: [150,300,450,600], h: 720, d: 570 },
  /* ─── WALL (standard depth 300) ─── */
  { id: "wall-standard",     label: "Wall Unit",               cat: "Wall", widths: [150,260,300,350,400,450,500,550,600,700,800,900,1000,1100,1200], h: 720, d: 300,
    heights: [575, 720, 900] },
  { id: "wall-bridge",       label: "Bridging Unit",           cat: "Wall", widths: [600,800,900,1000,1100,1200], h: 360, d: 300,
    heights: [288, 300, 360] },
  { id: "wall-mw-housing",   label: "Microwave Housing",       cat: "Wall", widths: [600], h: 720, d: 300 },
  { id: "wall-extractor",    label: "Extractor Housing",       cat: "Wall", widths: [600,900], h: 360, d: 300 },
  { id: "wall-corner",       label: "Wall Corner · Standard",  cat: "Wall", widths: [600,700,800,900], h: 720, d: 300, blanks: [300] },
  { id: "wall-corner-l",     label: "Wall Corner · L-Shaped",  cat: "Wall", widths: [600], h: 720, d: 600 },
  { id: "wall-corner-diag",  label: "Wall Corner · Diagonal",  cat: "Wall", widths: [600], h: 720, d: 600 },
  { id: "wall-boiler",       label: "Boiler Housing",          cat: "Wall", widths: [600], h: 720, d: 300 },
  { id: "wall-end-curved",   label: "End Unit · Curved",       cat: "Wall", widths: [300], h: 720, d: 300 },
  { id: "wall-end-angled",   label: "End Unit · Angled",       cat: "Wall", widths: [300], h: 720, d: 300 },
  { id: "wall-wine",         label: "Wine Rack (Wall)",        cat: "Wall", widths: [150,300,450,600], h: 720, d: 300 },
  /* ─── TALL (heights 1970 + 2150) ─── */
  { id: "tall-ff-housing",   label: "Fridge / Freezer Housing",cat: "Tall", widths: [600], h: 1970, d: 570, tallHeights: [1970, 2150] },
  { id: "tall-oven-single",  label: "Tall Oven · Single",      cat: "Tall", widths: [600], h: 1970, d: 570, tallHeights: [1970, 2150] },
  { id: "tall-oven-mw",      label: "Tall Oven · Single + MW", cat: "Tall", widths: [600], h: 1970, d: 570, tallHeights: [1970, 2150] },
  { id: "tall-oven-combimw", label: "Tall Oven · Single + Combi MW", cat: "Tall", widths: [600], h: 1970, d: 570, tallHeights: [1970, 2150] },
  { id: "tall-oven-double",  label: "Tall Oven · Double",      cat: "Tall", widths: [600], h: 1970, d: 570, tallHeights: [1970, 2150] },
  { id: "tall-larder",       label: "Larder",                  cat: "Tall", widths: [150,300,400,450,500,600,800,900,1000,1200], h: 1970, d: 570, tallHeights: [1970, 2150] },
  { id: "tall-larder-pullout", label: "Larder · Open / Pull-out",cat:"Tall", widths: [300,400,450,500,600], h: 1970, d: 570, tallHeights: [1970, 2150] },
  { id: "tall-wine",         label: "Wine Rack (Tall)",        cat: "Tall", widths: [150,200,250,300], h: 1970, d: 570, tallHeights: [1970, 2150] },
  /* ─── DRESSER ─── */
  { id: "dresser-glazed",    label: "Glazed Dresser",          cat: "Dresser", widths: [600,800,1000,1200], h: 1970, d: 320 },
  { id: "dresser-open",      label: "Open Dresser",            cat: "Dresser", widths: [600,800,1000,1200], h: 1970, d: 320 },
  { id: "dresser-mantle",    label: "Mantle Dresser",          cat: "Dresser", widths: [900,1000,1100,1200], h: 1970, d: 320 },
];

/** Default carcass finishes with price groups 1-5. Verbatim
 *  DEFAULT_CARCASS_FINISHES at kos-qb-v3.js:150–163. Fallback when the
 *  supplier API doesn't return its own list. */
export interface CarcassFinish {
  name: string;
  priceGroup: 1 | 2 | 3 | 4 | 5;
  hex: string;
}

export const DEFAULT_CARCASS_FINISHES: readonly CarcassFinish[] = [
  { name: "White",                priceGroup: 1, hex: "#f3f3f3" },
  { name: "Light Grey",           priceGroup: 1, hex: "#d3d3d3" },
  { name: "Dust Grey",            priceGroup: 2, hex: "#9a9a9a" },
  { name: "Anthracite",           priceGroup: 2, hex: "#444" },
  { name: "Cashmere",             priceGroup: 2, hex: "#d0c8be" },
  { name: "Bardolino Oak",        priceGroup: 3, hex: "#8b6f47" },
  { name: "Halifax Natural Oak",  priceGroup: 3, hex: "#a17c52" },
  { name: "Sonoma Oak",           priceGroup: 3, hex: "#b69876" },
  { name: "Truffle Brown",        priceGroup: 3, hex: "#5d4438" },
  { name: "Walnut",               priceGroup: 4, hex: "#5d4030" },
  { name: "Black",                priceGroup: 4, hex: "#181818" },
  { name: "Sand Pine",            priceGroup: 5, hex: "#c2a878" },
];

/** Front edging options — verbatim EDGING_OPTIONS at kos-qb-v3.js:165–169. */
export const EDGING_OPTIONS: readonly string[] = [
  "Matching carcass", "White ABS", "Cream ABS", "Light Grey ABS",
  "Dust Grey ABS", "Anthracite ABS", "Black ABS",
  "Oak veneer", "Walnut veneer", "Cashmere ABS", "Match door colour",
];

/** Full supplier registry — verbatim SUPPLIER_MODELS at kos-qb-v3.js:58–77.
 *  Used by the cabinet/door pickers' supplier dropdowns (which show all
 *  six suppliers); distinct from the four-tab `SUPPLIERS` shown in the
 *  Products panel pill bar. */
export interface PickerSupplier {
  key: string;        // canonical name (matches item.supplier)
  model: "completes" | "mixmatch";
  colour: string;
  defaultDiscPct: number;
  disabled?: boolean;
  note?: string;
  /** Capability flag — true when this supplier exposes a live finishes
   *  endpoint at /api/{finishesSlug}/finishes. Drives the session-defaults
   *  strip's finishes source: live fetch vs DEFAULT_CARCASS_FINISHES
   *  fallback. Flip to true (and set finishesSlug) when a new endpoint
   *  ships — the strip picks it up with no further code changes. */
  hasFinishesEndpoint?: boolean;
  /** URL slug under /api/ for the finishes endpoint (e.g. "dtn" → /api/dtn/finishes). */
  finishesSlug?: string;
}

export const PICKER_SUPPLIERS: readonly PickerSupplier[] = [
  { key: "TKC",                     model: "completes", colour: "#e58200", defaultDiscPct: 0  },
  { key: "Trade Kitchens by DTN",   model: "completes", colour: "#16a34a", defaultDiscPct: 0,  note: "+15% retailer markup pre-applied; default discount 0%", hasFinishesEndpoint: true, finishesSlug: "dtn" },
  { key: "LDL",                     model: "mixmatch",  colour: "#0ea5e9", defaultDiscPct: 0,  note: "Customer List = retailer trade price; no separate list/RRP" },
  { key: "PWS",                     model: "mixmatch",  colour: "#c8102e", defaultDiscPct: 45, disabled: true, note: "Not yet connected on this platform" },
  { key: "Omega",                   model: "mixmatch",  colour: "#1a3a5c", defaultDiscPct: 40 },
  { key: "Manual",                  model: "mixmatch",  colour: "#71717a", defaultDiscPct: 0  },
];

/** Normalised board-finish row used by the session-defaults strip and
 *  consumed by the cabinet picker. Both the live DTN endpoint and the
 *  DEFAULT_CARCASS_FINISHES fallback are projected into this shape so
 *  callers don't branch on source. */
export interface SessionFinish {
  /** Stable key for LS persistence. DTN: f.code · DEFAULT: slug of f.name. */
  code: string;
  /** Display name shown in the cabinet picker's finish field. */
  name: string;
  /** Full label shown in the strip dropdown. DTN: "Brand · Name · Structure"
   *  · DEFAULT: equals name. */
  label: string;
  /** Price group 1-5 (drives cabinet's `carcassPriceGroup`). */
  pg: 1 | 2 | 3 | 4 | 5;
}

/** TKC door range — shape returned by /api/catalogue/door-ranges.
 *  `colours` is the legacy flat union list used by callers that don't
 *  care about stock-type grouping (range cards in the products panel,
 *  fuzzy-match search). The door picker uses `coloursByType` to render
 *  Stocked / Paint to Order / Signature / Bespoke optgroups (2026-05-26). */
export interface TkcRange {
  name: string;
  colours: string[];
  /** Optional for backward compat — pre-2026-05-26 endpoints didn't
   *  return this. When absent the picker treats every colour as stock.
   *  `primed` is always an empty array — primed doors ship unfinished,
   *  so the tier has no colour grouping; it lives in the same shape only
   *  so the card-builder can address every tier uniformly. */
  coloursByType?: Record<DoorType, string[]>;
  /** Per-stockType "are there actual product rows tagged with this tier?"
   *  flag — drives whether the products-panel renders a PTO / Bespoke /
   *  Signature / Primed card for the range (added 2026-05-26). Supplier-
   *  level PTO palette alone does NOT flip pto:true here — only
   *  catalogue_products rows with specification.stockType matching do. */
  hasProductsByType?: Record<DoorType, boolean>;
  productCount: number;
  catalogueRange: string;
}

/** TKC catalogue size — shape returned by /api/tkc/door-sizes
 *  (modules/tkc_search.js:472). */
export interface DoorSize {
  sku: string;
  name?: string;
  h: number;
  w: number;
  d?: number;
  price: number;
  range?: string;
  doorRange?: string;
  doorColour?: string;
}

export interface QuoteZone {
  id: string;
  name: string;
  note: string;
  /** Retailer-written plain-English description shown on customer-facing PDFs
   *  in place of the itemised SKU breakdown, when this zone's `zoneSummary`
   *  toggle is on. Empty string falls back to a generic "N items" line. */
  summary: string;
  items: QuoteItem[];
}

export interface QuoteBuilderState {
  version: 3;
  /** Local client id — generated when the quote first exists in the
   *  browser. Stable across sessions; persists in the LS cache. The
   *  server stores it in `quotes.client_id` so re-syncs can match. */
  id: string;
  /** Server's UUID for this quote once it's been pushed to PG. Empty
   *  string until first successful POST. Used to choose POST vs PUT. */
  serverId?: string;
  /** The `quotes.version` value this copy was loaded (or last saved) at.
   *  Sent back as `baseVersion` so the server can refuse a stale write —
   *  see the last-write-wins guard in modules/portal_quotes.js.
   *
   *  NOT the same thing as `version: 3` above: that is the localStorage
   *  schema version and is a compile-time literal. Conflating the two would
   *  send baseVersion:3 on every save and defeat the guard entirely. */
  serverVersion?: number;
  reference: string;
  customer: string;
  customerId?: string;
  customerEmail?: string;
  customerPhone?: string;
  project: string;
  /** ISO date YYYY-MM-DD. */
  date: string;
  validDays: number;
  vatRate: number;
  status: QbStatus;
  notes: string;
  attachedDesigns: string[];
  groupBy: GroupBy;
  zones: QuoteZone[];
  /** Per-category picker defaults + remember toggles. Mirrors source's
   *  per-quote `q.defaults` (kos-qb-v3.js:218 / defaultDefaults() line 201). */
  defaults: QuoteDefaults;
  supplierDiscounts: Record<string, number>;
  /** Per-zone toggle: render zone as one-line summary on customer PDF. */
  zoneSummary: Record<string, boolean>;
  deliveryChargePence: number;
  termsAndConditions?: string;
  /** Recorded approval metadata when the customer confirms a quote in
   *  showroom / email / phone / signed PDF. Sets `status='approved'` as a
   *  side-effect. Source: kos-qb-v3.js:6776-6791 `_qbv3SaveSignature`. */
  customerSignature?: { signedBy: string; signedAt: string; method: string };
  /** Cover note shown above the line items on the customer-facing quote.
   *  Seeded from the retailer's Settings default; editable per quote. */
  introNote?: string;
  /** Closing note shown below the totals (above T&Cs) on the customer quote. */
  closingNote?: string;
  lastSavedAt: number;
  /** Last successful PG sync (server-side updatedAt). 0 if never synced. */
  lastServerSyncAt?: number;
  /** First time the customer opened this quote in their portal (ms epoch).
   *  Undefined/0 until then. Server-owned (stamped on the customer view);
   *  read-only on the client — drives the QB "Seen" indicator. */
  openedAt?: number;
  createdAt: number;
}

export interface QuoteTotals {
  listCostPence: number;
  tradeCostPence: number;
  discountPence: number;
  /** Item retail subtotal, ex-VAT, ex-delivery — used as the margin denominator. */
  retailSubPence: number;
  profitPence: number;
  marginPct: number;
  /** Delivery charge ex-VAT (mirrors source `q.deliveryCharge`). 0 when no charge. */
  deliveryPence: number;
  /** Delivery × VAT rate. 0 when no delivery charge. */
  deliveryVatPence: number;
  /** VAT on item subtotal only — does NOT include delivery VAT. */
  vatAmountPence: number;
  /** Items + items VAT + delivery + delivery VAT. */
  grandTotalPence: number;
  lineCount: number;
  /** Per-supplier breakdown for the supplier-grouped totals card. */
  bySupplier: Record<string, { tradePence: number; retailPence: number; items: number; qty: number }>;
}

/** Kit-Q assistant action shape — server returns `actions[]`. */
export type KitQAction =
  | { tool: "add_items"; zoneName?: string; items: Array<Partial<QuoteItem> & { cabinetType?: string; construction?: string }> }
  | { tool: "update_items"; updates: Array<Partial<QuoteItem> & { itemId: string }> }
  | { tool: "remove_items"; itemIds: string[] }
  | { tool: "add_zone"; name: string; note?: string }
  | { tool: "duplicate_zone"; sourceZoneName: string; newName?: string }
  | { tool: "set_supplier_discount"; supplier: string; discPct: number }
  | { tool: "apply_markup"; mode: MarkupMode; value: number; scope?: { zoneName?: string; category?: ItemCategory; supplier?: string } }
  | { tool: "set_quote_meta"; meta: Partial<Pick<QuoteBuilderState, "customer" | "project" | "reference" | "validDays" | "vatRate" | "status">> };

export interface KitQResponse {
  reply: string;
  actions?: KitQAction[];
}

// ── W8 cabinet picker (DTN-only Phase 1) ─────────────────────────────────
//
// Port of the W8 cabinet UI from kos-qb-v3.js (CHIPS + DRAWER_PACK_CONFIGS
// + cab/accessory response shapes + spec state). This is the SOT picker
// shown in the screenshots; the previous non-W8 port has been retired.
//
// Phase 1 scope:
//   • Cabinet chips + drawer-packs chip only (15 entries)
//   • Accessory + extras chips deferred to Phase 2
//   • DTN-only (TKC/LDL/Custom keep their existing paths)

/** Response shape from `/api/dtn/cabinets` — see
 *  kitchenos-api/modules/dtn.js:211–215. */
export interface DtnCabRow {
  code: string;
  desc: string;
  unit_type: string;
  unit_label: string;
  section: string | null;
  /** Height (mm). */
  h: number;
  /** Depth (mm). */
  d: number;
  /** Wall thickness or "wall" flag — passthrough from catalogue. */
  wall: number | null;
  /** Price-group prices in £. Plate is PG1..PG5, with PG1 carrying the
   *  deepest discount per the W8 ladder (40/30/20/10/0 %). */
  pg1: number;
  pg2: number;
  pg3: number;
  pg4: number;
  pg5: number;
}

/** Response shape from `/api/dtn/accessories` — see
 *  kitchenos-api/modules/dtn.js:228–232. */
export interface DtnAccessoryRow {
  code: string;
  desc: string;
  unit_type: string;
  unit_label: string;
  section: string | null;
  price: number;
}

/** Response shape from `/api/dtn/finishes` — see
 *  kitchenos-api/modules/dtn.js:240–250. Discount % is the per-PG ladder
 *  used by the qbv3w8-pgbadge. */
export interface DtnFinishRow {
  brand: string;
  name: string;
  code: string;
  structure: string;
  disc: number;
  pg: 1 | 2 | 3 | 4 | 5;
  label: string;
}

/** Chip kind in the W8 left-pane strip. Phase 1 only emits `cabinet` and
 *  `drawer_packs`; `accessory` and `extras` are part of Phase 2. */
export type W8ChipKind = "cabinet" | "drawer_packs" | "accessory" | "extras";

/** Verbatim port of CHIPS at kos-qb-v3.js:10780–10800. `kind` chooses
 *  which click-handler the list-card uses; `type` is the unit_type
 *  filter passed to `/cabinets?type=`; `section` is an additional
 *  client-side keyword filter applied after the response. */
export interface W8Chip {
  id: string;
  kind: W8ChipKind;
  type: string;
  section: string | null;
  label: string;
}

/** Phase 1 chip set — 14 cabinet chips + 1 drawer-packs chip.
 *  Excludes the 5 accessory chips (Hinges, Antaro, Legrabox, Elite/Meta,
 *  MFC Pure) and the Extras chip — Phase 2. Verbatim ids/labels from SOT
 *  so the LS / quote line metadata stays grep-compatible with single-file
 *  exports. */
export const W8_CABINET_CHIPS: readonly W8Chip[] = [
  { id: "base",            kind: "cabinet",      type: "base",          section: null,        label: "Base" },
  { id: "base_corner",     kind: "cabinet",      type: "base",          section: "CORNER",    label: "Corner Base" },
  { id: "drawer_packs",    kind: "drawer_packs", type: "drawer_packs",  section: null,        label: "Drawer Packs" },
  { id: "base_appliance",  kind: "cabinet",      type: "base",          section: "APPLIANCE", label: "Appliance" },
  { id: "wall_sw",         kind: "cabinet",      type: "sw",            section: null,        label: "575 Wall" },
  { id: "wall_mw",         kind: "cabinet",      type: "mw",            section: null,        label: "720 Wall" },
  { id: "wall_tw",         kind: "cabinet",      type: "tw",            section: null,        label: "900 Wall" },
  { id: "larder_sl",       kind: "cabinet",      type: "sl",            section: null,        label: "1825 Larder" },
  { id: "larder_ml",       kind: "cabinet",      type: "ml",            section: null,        label: "1970 Larder" },
  { id: "larder_tl",       kind: "cabinet",      type: "tl",            section: null,        label: "2150 Larder" },
  { id: "dresser",         kind: "cabinet",      type: "dresser",       section: null,        label: "Dresser" },
  { id: "winerack",        kind: "cabinet",      type: "winerack",      section: null,        label: "Winerack" },
  { id: "curved_panels",   kind: "cabinet",      type: "curved_panels", section: null,        label: "Curved & Panels" },
  { id: "gola",            kind: "cabinet",      type: "gola",          section: null,        label: "Gola" },
];

/** Verbatim port of DRAWER_PACK_CONFIGS at kos-qb-v3.js:11119–11127. */
export interface DrawerPackConfig {
  id: string;
  label: string;
  desc: string;
  widths: readonly number[];
  drawerCount: number;
  subcats: readonly string[];
}

export const DRAWER_PACK_CONFIGS: readonly DrawerPackConfig[] = [
  { id: "dp_2pan",     label: "2 Drawer Pack",            desc: "2 x Pan Drawers",                                     widths: [400, 500, 600, 800, 900, 1000], drawerCount: 2, subcats: ["720 Drawer Packs", "Drawer Packs"] },
  { id: "dp_2pan_int", label: "2 Drawer Pack + Internal", desc: "1 x Internal cutlery drawer + 1 x Medium + 1 x Pan", widths: [400, 500, 600, 800, 900, 1000], drawerCount: 2, subcats: ["720 Drawer Packs", "Drawer Packs"] },
  { id: "dp_3_1cut",   label: "3 Drawer Pack",            desc: "1 x Cutlery + 2 x Pan Drawers",                      widths: [400, 500, 600, 800, 900, 1000], drawerCount: 3, subcats: ["3 Drawer Pack"] },
  { id: "dp_3_2cut",   label: "3 Drawer Pack",            desc: "2 x Cutlery + 1 x Pan Drawer",                       widths: [500, 600],                       drawerCount: 3, subcats: ["3 Drawer Pack"] },
  { id: "dp_4_3cut",   label: "4 Drawer Pack",            desc: "3 x Cutlery + 1 x Pan Drawer",                       widths: [500, 600],                       drawerCount: 4, subcats: ["4 Drawer Pack"] },
  { id: "dp_4_4cut",   label: "4 Drawer Pack",            desc: "4 x Cutlery Drawers",                                widths: [500, 600],                       drawerCount: 4, subcats: ["4 Drawer Pack"] },
  { id: "dp_5_5cut",   label: "5 Drawer Pack",            desc: "5 x Cutlery Drawers",                                widths: [400, 500, 600, 800, 900, 1000], drawerCount: 5, subcats: ["Drawer Packs"] },
];

/** Virtual cab synthesised by the Drawer Packs chip render. Carries the
 *  picked DRAWER_PACK_CONFIGS row + bundled real-cab carcass code so the
 *  Add-to-quote path can resolve carcass + drawer-box pricing. */
export interface W8VirtualCab {
  code: string;
  desc: string;
  unit_type: "drawer_packs";
  unit_label: "Drawer Packs";
  section: string;
  width_mm: number;
  height_mm: number;
  depth_mm: number;
  h: number;
  d: number;
  pg1: number;
  pg2: number;
  pg3: number;
  pg4: number;
  pg5: number;
  _dpConfig: DrawerPackConfig;
  _carcassCode: string | null;
  _carcassDesc: string | null;
  _carcass: DtnCabRow | null;
}

/** Discriminated cab payload carried by the selected-cab pubsub —
 *  either a real DTN cab row from the endpoint or a synthesised
 *  drawer-pack virtual cab. */
export type W8Cab = DtnCabRow | W8VirtualCab;

/** Config kind detector output — verbatim port of detectConfigKind
 *  at kos-qb-v3.js:11309–11318. */
export type W8ConfigKind = "standard" | "drawer_pack" | "tall_oven" | "gola" | "gola_wall";

/** W8 picker working state — verbatim port of `W8.spec` at
 *  kos-qb-v3.js:11328–11337 with type-safe flags. */
export interface W8Spec {
  width: number | "";
  height: number | "";
  depth: number | "";
  hinge_side: "" | "L" | "R" | "B" | "N";
  finish: DtnFinishRow | null;
  finish_pg: 1 | 2 | 3 | 4 | 5;
  edging: string;
  notes: string;
  qty: number;
  zoneId: string;
  drawer_config: string;
  drawer_box: string;
  drawers_below: "" | "1" | "2";
  gable_reduction: "" | "L" | "R" | "B";
  is_drawerline: boolean;
  handleless: boolean;
  top_door: string;
  bottom_door: string;
}
